import { useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'

/**
 * Sheet — a bottom-sheet modal. Backdrop fades in, the panel slides up from
 * the bottom edge (the natural reach zone on a phone). Used for the quantity
 * keypad, the cash-tender pad, the Browse catalogue and Help.
 *
 *   open      render gate — nothing mounts when false
 *   onClose   tap-backdrop / X / Esc
 *   title     bold heading (also the dialog's accessible name)
 *   subtitle  muted sub-heading (e.g. "120 Tab in stock")
 *   footer    sticky action area below the scroll body (e.g. a Confirm button)
 *
 * It is a real modal dialog: focus moves into it on open and returns to the
 * control that opened it on close. Height is capped and the body scrolls, so
 * a long catalogue never pushes the footer off-screen. Safe-area padding
 * keeps the footer clear of the home indicator.
 */
export default function Sheet({ open, onClose, title, subtitle, children, footer, maxHeight = '88%' }) {
  const titleId = useId()
  const panelRef = useRef(null)
  // Callers pass inline onClose functions; reading the latest through a ref
  // keeps the effect below tied to open/close only, so focus isn't pulled
  // back to the panel every time the parent re-renders (e.g. on each keypad
  // press or cart change).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return undefined
    const opener = document.activeElement
    const onKey = (event) => { if (event.key === 'Escape') onCloseRef.current?.() }
    document.addEventListener('keydown', onKey)
    // Focus the panel itself (not the first input) so a keypad sheet doesn't
    // pop the phone keyboard over the numbers.
    requestAnimationFrame(() => panelRef.current?.focus({ preventScroll: true }))
    return () => {
      document.removeEventListener('keydown', onKey)
      opener?.focus?.({ preventScroll: true })
    }
  }, [open])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-brand-800/50 backdrop-blur-[2px] pc-fade-in" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className="pc-sheet relative bg-white rounded-t-3xl ring-1 ring-black/5 shadow-2xl shadow-brand-800/30 flex flex-col pc-sheet-up outline-none"
        style={{ maxHeight, paddingBottom: 'max(env(safe-area-inset-bottom), 0.5rem)' }}
      >
        {/* Grab handle */}
        <div className="pt-2.5 pb-1 flex justify-center shrink-0" aria-hidden="true">
          <div className="h-1.5 w-10 rounded-full bg-sage-200" />
        </div>

        {(title || onClose) && (
          <div className="flex items-start justify-between px-5 pb-3 pt-1 shrink-0">
            <div className="min-w-0 pr-2">
              {title && <h2 id={titleId} className="font-display text-xl font-semibold text-sage-900 truncate leading-tight">{title}</h2>}
              {subtitle && <p className="text-sm font-medium text-sage-600 truncate mt-0.5">{subtitle}</p>}
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="h-11 w-11 -mr-2 -mt-1.5 grid place-items-center rounded-xl text-sage-500 hover:bg-sage-100 active:bg-sage-100 active:text-sage-800"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        <div className="overflow-y-auto px-5 min-h-0">{children}</div>

        {footer && <div className="px-5 pt-3 shrink-0">{footer}</div>}
      </div>
    </div>
  )
}
